import type { ApolloQueryResult } from '@apollo/client/core'
import { parse } from 'graphql'
import { useQuery } from 'glimmer-apollo'

export interface ProgramResponse {
  program: ProgramType
}

export interface ProgramQueryVariables {
  id: string
}

export async function fetchProgram(
  context: Object,
  variables: ProgramQueryVariables
): Promise<ApolloQueryResult<ProgramResponse> | undefined> {
  const programQuery = useQuery<ProgramResponse, ProgramQueryVariables>(context, () => [
    query,
    {
      variables,
      onError: (error) => {
        throw new Error(`Failed to fetch program. ${error.message}`, { cause: error })
      }
    }
  ])

  return programQuery.refetch()
}

const query = parse(`
  query Program($id: ID!) {
    program(id: $id) {
      id
      layoutName
      locale
      pricingTransparency
      provider {
        description
        name
        id
      }
      tracking {
        ga
        fb
      }
      theme
      ... on ProgramTiles {
        title
        pricingTransparency
        tiles {
          image
          subtitle
          title
          program {
            layoutName
            id
            ... on BookingFlow {
              activity {
                id
                name
                description
                imageUrls
                type
              }
            }
          }
        }
      }
      ... on BookingFlow {
        orderTaxesFeesBreakdownMode
        relatedPrograms {
          id
          cta
          title
          subtitle
          program {
            id
            layoutName
          }
        }
        activity {
          id
          name
          description
          imageUrls
          type
          questionActivityConfigurations {
            id
            isRequired
            question {
              id
              text
              type
              perGuest
              hint
              answerDefaultValue
              whitelistedCategories
              ... on ChoiceQuestion {
                options {
                  id
                  text
                  hint
                  chargePerGuest
                  followupQuestions {
                    id
                    text
                    type
                    perGuest
                    hint
                    answerDefaultValue
                  }
                }
              }
            }
          }
          tickets {
            id
            name
            description
            imageUrl
          }
          reviewMeta {
            count
            avgRating
            fiveStar
          }
          reviews(pageSize: 3, onlyReviewsWithComments: true, ratings: [5]) {
            entries {
              ... on Review {
                id
                name
                comment
                reviewedAt
              }
            }
          }
        }
        initialSelectionDescription
        overrideActivityImage
        overrideTextActivityName
        overrideTextDescription
        overrideTextTicketHeader
        overrideTimeTitleText
        overrideDurationTitleText
        overrideConfirmationScreenText
        overrideConfirmationScreenTitle
        bookingPortalOverrideDescription
        bookingPortalOverrideLinkLabel
        bookingPortalShowConfirmation
        promoCodeDisplay
        promoCodeLocation
        overridePromocodeLinkText
        externalTocLink
        externalTocName
        calendarStartDate
        disableCountdownClock
      }
    }
  }
`)
